import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Baie coulissante', to: '/reparation-baie-coulissante-herault/' },
  { label: 'Roulettes & galets' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Remplacement de roulettes et galets de baie coulissante dans l\'Hérault',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'AdministrativeArea', name: 'Hérault' },
  description: 'Remplacement de roulettes et galets de baie coulissante toutes marques dans l\'Hérault. Intervention sous 24-48h.',
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Comment savoir si les roulettes de ma baie coulissante sont usées ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Les signes d\'usure sont clairs : la baie devient dure à ouvrir ou à fermer, elle émet un bruit de raclement métallique lors du coulissement, ou elle ne glisse plus de manière fluide. Si vous devez forcer pour manœuvrer votre baie coulissante, les roulettes ou galets sont probablement en fin de vie et doivent être remplacés rapidement pour éviter d\'endommager le rail.',
      },
    },
    {
      '@type': 'Question',
      name: 'Remplacez-vous les roulettes de toutes les marques de baies ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous remplaçons les roulettes et galets sur l\'ensemble des marques de menuiseries aluminium : Technal, Sepalumic, Schüco, K-Line, Wicona, et toutes les autres. Nous disposons d\'un stock de pièces détachées couvrant les principaux fabricants pour une intervention rapide dans l\'Hérault.',
      },
    },
  ],
};

const contentBlocks = [
  {
    image: '/images/roulettes-block1.webp',
    imageAlt: 'Roulettes et galets uses de baie coulissante dans l Herault',
    children: (
      <>
        <h2>Roulettes et galets : les pièces d'usure de votre baie coulissante</h2>
        <p>
          Les roulettes et les galets sont les composants les plus sollicités de votre baie vitrée coulissante. À chaque ouverture et fermeture, ils supportent le poids du vantail — qui peut atteindre plusieurs centaines de kilos pour les grandes baies aluminium — tout en assurant un coulissement fluide le long du rail. Avec le temps, l'encrassement, l'humidité et l'usure mécanique finissent par détériorer ces pièces.
        </p>
        <h3>Les signes d'usure à ne pas ignorer</h3>
        <p>
          Plusieurs indices vous alertent sur l'état de vos roulettes et galets. Le plus évident est la difficulté à manœuvrer la baie : si vous devez forcer pour l'ouvrir ou la fermer, c'est que les roulettes ne tournent plus librement. Un bruit de raclement ou de grincement métallique pendant le coulissement est un autre signal fort. Dans les cas les plus avancés, la baie peut même sortir de son rail, créant un risque pour votre sécurité et celle de votre famille. À Montpellier, comme sur le littoral héraultais où l'air salin peut accélérer la corrosion, ces symptômes doivent être pris au sérieux.
        </p>
      </>
    ),
  },
  {
    image: '/images/roulettes-block2.png',
    imageAlt: 'Remplacement de roulettes de baie coulissante par un technicien DFPRO Herault',
    imagePosition: 'right' as const,
    children: (
      <>
        <h3>Notre processus de remplacement</h3>
        <p>
          Le remplacement des roulettes et galets d'une baie coulissante nécessite un savoir-faire technique précis. Nos techniciens DFPRO Hérault commencent par dégonder le vantail en toute sécurité. Ils retirent ensuite les anciennes roulettes, nettoient les logements, et installent les nouvelles pièces parfaitement adaptées à votre modèle de menuiserie. Le vantail est ensuite reposé, et un réglage minutieux est effectué pour garantir un coulissement fluide et silencieux. L'intervention dure généralement entre une et deux heures.
        </p>
        <h3>Toutes marques, toutes configurations</h3>
        <p>
          Chaque fabricant de menuiserie aluminium utilise ses propres systèmes de roulettes. Les baies Technal n'ont pas les mêmes galets que les baies Sepalumic ou Schüco. Nos techniciens connaissent ces spécificités et disposent d'un catalogue de pièces couvrant les principales marques du marché. Qu'il s'agisse de roulettes simples, de galets à roulement à billes, de chariots de roulement réglables ou de systèmes à galets jumelés, nous avons la pièce qu'il vous faut.
        </p>
      </>
    ),
  },
];

const fullWidthSections = [
  <>
    <h3>Pourquoi ne pas attendre ?</h3>
    <p>
      Retarder le remplacement de roulettes usées peut avoir des conséquences coûteuses. Des roulettes défectueuses endommagent progressivement le rail de coulissement, déforment le profilé aluminium du vantail, et peuvent même causer une casse du vitrage si le vantail se bloque brutalement. Une intervention rapide permet d'éviter ces désagréments et de préserver l'intégrité de votre baie coulissante. Avec DFPRO Hérault, vous bénéficiez d'un diagnostic gratuit, d'un devis transparent et d'une réparation garantie 10 ans dans tout le département.
    </p>
    <p>
      Pour en savoir plus sur l'ensemble de nos services, consultez notre page dédiée à la <Link to="/reparation-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparation de baie coulissante dans l'Hérault</Link>. Si votre baie coince au point de ne plus coulisser du tout, nous intervenons également sur les <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">baies coulissantes bloquées</Link>.
    </p>
  </>,
];

export default function RoulettesGalets() {
  return (
    <>
      <SEO
        title="Roulettes & galets de baie coulissante Hérault | DFPRO"
        description="Roulettes ou galets de baie coulissante usés dans l'Hérault ? Remplacement rapide toutes marques. Devis gratuit, garantie 10 ans. Appelez le 09 80 80 22 42."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/changer-roulettes-galets-baie-coulissante-herault/"
      />
      <ServicePageLayout
        title="Remplacement de roulettes et galets de baie coulissante dans l'Hérault"
        subtitle="Roulettes usées, coulissement dur, bruit de raclement ? Nos techniciens remplacent vos roulettes et galets sous 24-48h dans tout l'Hérault."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        heroImage="/images/roulettes-hero.jpg"
        heroImageAlt="Roulettes et galets de baie coulissante DFPRO Herault"
        contentBlocks={contentBlocks}
        fullWidthSections={fullWidthSections}
      />
    </>
  );
}